import { useCallback, useMemo, useState } from 'react';

/** Public shape returned by `usePinnedMessages`. */
export type UsePinnedMessagesResult = {
	pinnedIds: string[];
	isPinned: (id: string) => boolean;
	togglePin: (id: string) => void;
	pin: (id: string) => void;
	unpin: (id: string) => void;
	clear: () => void;
};

/**
 * Per-session state for message IDs the user has pinned on the right rail.
 *
 * Intentionally non-persistent (see `./README.md`): pins reset on reload
 * because coaching context is call-scoped.
 */
export function usePinnedMessages(initial: ReadonlyArray<string> = []): UsePinnedMessagesResult {
	const [pinnedIds, setPinnedIds] = useState<string[]>(() => [...initial]);

	const isPinned = useCallback((id: string): boolean => pinnedIds.includes(id), [pinnedIds]);

	const pin = useCallback((id: string): void => {
		setPinnedIds(prev => (prev.includes(id) ? prev : [...prev, id]));
	}, []);

	const unpin = useCallback((id: string): void => {
		setPinnedIds(prev => prev.filter(existing => existing !== id));
	}, []);

	const togglePin = useCallback((id: string): void => {
		setPinnedIds(prev => (prev.includes(id) ? prev.filter(existing => existing !== id) : [...prev, id]));
	}, []);

	const clear = useCallback((): void => {
		setPinnedIds([]);
	}, []);

	return useMemo(
		() => ({ pinnedIds, isPinned, togglePin, pin, unpin, clear }),
		[pinnedIds, isPinned, togglePin, pin, unpin, clear]
	);
}
